import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import TestimonialsSlider from '../components/TestimonialsSlider';
import FAQSection from '../components/FAQSection';
import ContactModal from '../components/ContactModal';
import MobileSlider from '../components/MobileSlider';
import { useToast } from '../hooks/use-toast';
import { ArrowRight, Shield, CheckCircle, Award, Star, Clock, Users, FileCheck, TrendingUp, Mail, Phone, User, MessageSquare, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { submitLead, getGeoLocation, buildLeadPayload, getCountryCodeFromDialCode } from '../services/api';
import { openZendeskChat } from '../utils/chat';
import { validateEmail, validatePhone, validateName, validateRequired } from '../utils/validation';

const LandingPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Process steps - 3 steps only
  const processSteps = [
    { icon: FileCheck, number: '01', title: 'Submit Your Details', desc: 'Share your trademark information through our simple form and get instant expert guidance.' },
    { icon: Shield, number: '02', title: 'We Handle Everything', desc: 'Our team conducts comprehensive searches, prepares documents, and files with the USPTO.' },
    { icon: Award, number: '03', title: 'Get Protected', desc: 'Receive your registered trademark certificate and enjoy complete brand protection.' }
  ];

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  const validateField = (field, value) => {
    let validation = { isValid: true, error: '' };

    switch (field) {
      case 'name':
        validation = validateName(value);
        break;
      case 'email':
        validation = validateEmail(value);
        break;
      case 'phone':
        validation = validatePhone(value, formData.countryCode);
        break;
      case 'description':
        validation = validateRequired(value, 'Description', 1);
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [field]: validation.error
    }));

    return validation.isValid;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Validate on change if field was already touched
    if (touched[field]) {
      validateField(field, value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    setTouched({
      name: true,
      email: true,
      phone: true,
      description: true
    });

    // Validate all fields
    const nameValid = validateField('name', formData.name);
    const emailValid = validateField('email', formData.email);
    const phoneValid = validateField('phone', formData.phone);
    const descValid = validateField('description', formData.description);

    if (!nameValid || !emailValid || !phoneValid || !descValid) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors in the form before submitting',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Fetch geolocation data
      const geoData = await getGeoLocation();
      
      // Extract numeric dial code from countryCode
      const dialCode = formData.countryCode.replace('+', '');
      
      // Build complete payload
      const payload = buildLeadPayload({
        name: formData.name,
        email: formData.email,
        countryDialCode: dialCode,
        phoneNumber: formData.phone,
        description: formData.description,
        packageTitle: 'Trademark Registration',
        packageAmount: 45
      }, geoData);

      // Submit lead
      const result = await submitLead(payload);

      if (result.success && result.statusCode === 200) {
        toast({
          title: 'Success!',
          description: result.message || 'Your request has been submitted successfully.'
        });
        
        // Reset form
        setFormData({ name: '', email: '', phone: '', countryCode: '+1', description: '' });
        
        // Redirect to application page with customerId
        if (result.customerId) {
          navigate(`/application/main?customerid=${result.customerId}`);
        }
      } else {
        toast({
          title: 'Error',
          description: result.message || 'Something went wrong. Please try again.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-900">
      {/* Custom Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-navy-900 shadow-md py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div>
              <img 
                src="/logo-header.png" 
                alt="Trademark Nexor" 
                className="h-12 w-auto hidden"
              />
              <img src="/assets/img/brand/logo-light.png" alt="Logo" className="h-12" />
              {/* <span className="text-2xl font-bold tracking-tight text-gold-400">Logo</span> */}
            </div>

            {/* Buttons */}
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => setIsModalOpen(true)}
                className="bg-brand-gradient text-white hover:opacity-90 font-semibold"
              >
                Register My Trademark
              </Button>
              <Button
                onClick={openZendeskChat}
                className="bg-navy-700 border-2 border-gold-400/60 text-brand-primary hover:bg-navy-800 font-semibold"
              >
                Chat Now
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Form */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            
            {/* Form First on Mobile, Content First on Desktop */}
            {/* Mobile Form (shows first) */}
            <div className="lg:hidden order-1">
              <div id="contact-form" className="bg-navy-700 rounded-2xl shadow-2xl p-8 animate-slide-up border border-navy-400/40">
                <h2 className="text-2xl font-bold text-gray-100 mb-6">Get Started Today</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-200 mb-2">Full Name *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                      <Input
                        type="text"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        onBlur={() => handleBlur('name')}
                        className={`pl-10 py-5 ${errors.name && touched.name ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-navy-500 focus:border-brand-primary focus:ring-brand-primary'}`}
                      />
                    </div>
                    {errors.name && touched.name && (
                      <div className="flex items-center text-xs text-red-600 mt-1">
                        <AlertCircle size={12} className="mr-1" />
                        {errors.name}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-200 mb-2">Email Address *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                      <Input
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        onBlur={() => handleBlur('email')}
                        className={`pl-10 py-5 ${errors.email && touched.email ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-navy-500 focus:border-brand-primary focus:ring-brand-primary'}`}
                      />
                    </div>
                    {errors.email && touched.email && (
                      <div className="flex items-center text-xs text-red-600 mt-1">
                        <AlertCircle size={12} className="mr-1" />
                        {errors.email}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-200 mb-2">Contact Number *</label>
                    <div className="flex gap-2">
                      <Select value={formData.countryCode} onValueChange={(value) => handleChange('countryCode', value)}>
                        <SelectTrigger className="w-24 py-5 border-navy-500">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="+1">+1</SelectItem>
                          <SelectItem value="+44">+44</SelectItem>
                          <SelectItem value="+91">+91</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="relative flex-1">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <Input
                          type="tel"
                          placeholder="123-456-7890"
                          value={formData.phone}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            handleChange('phone', value);
                          }}
                          onBlur={() => handleBlur('phone')}
                          maxLength={10}
                          className={`pl-10 py-5 ${errors.phone && touched.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-navy-500 focus:border-brand-primary focus:ring-brand-primary'}`}
                        />
                      </div>
                    </div>
                    {errors.phone && touched.phone && (
                      <div className="flex items-center text-xs text-red-600 mt-1">
                        <AlertCircle size={12} className="mr-1" />
                        {errors.phone}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-200 mb-2">Description</label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 text-gray-400" size={18} />
                      <Textarea
                        placeholder="Tell us about your trademark..."
                        value={formData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        onBlur={() => handleBlur('description')}
                        className={`pl-10 pt-3 h-24 resize-none ${errors.description && touched.description ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-navy-500 focus:border-brand-primary focus:ring-brand-primary'}`}
                      />
                    </div>
                    {errors.description && touched.description && (
                      <div className="flex items-center text-xs text-red-600 mt-1">
                        <AlertCircle size={12} className="mr-1" />
                        {errors.description}
                      </div>
                    )}
                  </div>

                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-brand-gradient text-white hover:opacity-90 py-5 text-lg font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Register My Trademark'}
                  </Button>
                </form>
              </div>
            </div>

            {/* Left Content (order-2 on mobile so it shows after form) */}
            <div className="space-y-6 animate-fade-in order-2 lg:order-1">
              <h1 className="text-5xl md:text-6xl font-bold text-gray-100 leading-tight">
                Protect Your Brand with{' '}
                <span className="bg-brand-gradient bg-clip-text text-transparent">
                  Professional Trademark Registration
                </span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed">
                Secure your business identity with our expert trademark registration services. Starting at just $45, we make brand protection affordable and hassle-free.
              </p>
              <div className="flex flex-wrap gap-6 pt-4">
                <div className="flex items-center space-x-2 group cursor-pointer">
                  <CheckCircle className="text-brand-primary group-hover:scale-125 group-hover:text-gold-400 transition-all duration-300" size={24} />
                  <span className="font-semibold text-gray-200">98% Success Rate</span>
                </div>
                <div className="flex items-center space-x-2 group cursor-pointer">
                  <Clock className="text-brand-primary group-hover:scale-125 group-hover:text-gold-400 transition-all duration-300" size={24} />
                  <span className="font-semibold text-gray-200">24-Hour Processing</span>
                </div>
                <div className="flex items-center space-x-2 group cursor-pointer">
                  <Users className="text-brand-primary group-hover:scale-125 group-hover:text-gold-400 transition-all duration-300" size={24} />
                  <span className="font-semibold text-gray-200">10,000+ Clients</span>
                </div>
              </div>
            </div>

            {/* Right Form (Desktop Only) */}
            <div className="hidden lg:block order-3 lg:order-2">
              <div id="contact-form" className="bg-navy-700 rounded-2xl shadow-2xl p-8 animate-slide-up border border-navy-400/40">
              <h2 className="text-2xl font-bold text-gray-100 mb-6">Get Started Today</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-200 mb-2">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      onBlur={() => handleBlur('name')}
                      className={`pl-10 py-5 ${errors.name && touched.name ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-navy-500 focus:border-brand-primary focus:ring-brand-primary'}`}
                    />
                  </div>
                  {errors.name && touched.name && (
                    <div className="flex items-center text-xs text-red-600 mt-1">
                      <AlertCircle size={12} className="mr-1" />
                      {errors.name}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-200 mb-2">Email Address *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      onBlur={() => handleBlur('email')}
                      className={`pl-10 py-5 ${errors.email && touched.email ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-navy-500 focus:border-brand-primary focus:ring-brand-primary'}`}
                    />
                  </div>
                  {errors.email && touched.email && (
                    <div className="flex items-center text-xs text-red-600 mt-1">
                      <AlertCircle size={12} className="mr-1" />
                      {errors.email}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-200 mb-2">Contact Number *</label>
                  <div className="flex gap-2">
                    <Select value={formData.countryCode} onValueChange={(value) => handleChange('countryCode', value)}>
                      <SelectTrigger className="w-24 py-5 border-navy-500">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="+1">+1</SelectItem>
                        <SelectItem value="+44">+44</SelectItem>
                        <SelectItem value="+91">+91</SelectItem>
                        <SelectItem value="+92">+92</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="relative flex-1">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                      <Input
                        type="tel"
                        placeholder="123-456-7890"
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          handleChange('phone', value);
                        }}
                        onBlur={() => handleBlur('phone')}
                        maxLength={10}
                        className={`pl-10 py-5 ${errors.phone && touched.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-navy-500 focus:border-brand-primary focus:ring-brand-primary'}`}
                      />
                    </div>
                  </div>
                  {errors.phone && touched.phone && (
                    <div className="flex items-center text-xs text-red-600 mt-1">
                      <AlertCircle size={12} className="mr-1" />
                      {errors.phone}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-200 mb-2">Description</label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 text-gray-400" size={18} />
                    <Textarea
                      placeholder="Tell us about your trademark..."
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      onBlur={() => handleBlur('description')}
                      className={`pl-10 pt-3 h-24 resize-none ${errors.description && touched.description ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-navy-500 focus:border-brand-primary focus:ring-brand-primary'}`}
                    />
                  </div>
                  {errors.description && touched.description && (
                    <div className="flex items-center text-xs text-red-600 mt-1">
                      <AlertCircle size={12} className="mr-1" />
                      {errors.description}
                    </div>
                  )}
                </div>

                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-brand-gradient text-white hover:opacity-90 py-5 text-lg font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Register My Trademark'}
                </Button>
              </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-navy-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-gray-100 mb-12 animate-fade-in">
              Why Choose Our <span className="bg-brand-gradient bg-clip-text text-transparent">Trademark Services</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { icon: Shield, title: 'Expert Team', desc: 'Experienced trademark attorneys and specialists guide you every step' },
                { icon: Clock, title: 'Fast Processing', desc: 'Applications filed within 24 hours with expedited options available' },
                { icon: Award, title: '98% Success Rate', desc: 'Industry-leading approval rate backed by thorough research' },
                { icon: TrendingUp, title: 'Affordable Pricing', desc: 'Starting at just $45 with transparent, no-hidden-fee pricing' }
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div key={idx} className="text-center p-6 bg-gradient-to-br from-navy-800 to-navy-700 rounded-xl shadow-lg hover:shadow-2xl transition-all hover:-translate-y-3 hover:scale-105 animate-scale-in group" style={{animationDelay: `${idx * 0.1}s`}}>
                    <div className="bg-brand-gradient w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-125 group-hover:-rotate-12 transition-all duration-500">
                      <Icon className="text-white" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-100 mb-2 group-hover:text-brand-primary transition-colors">{item.title}</h3>
                    <p className="text-base text-gray-300">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section - 3 Steps with Enhanced Styling */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-100 mb-6 animate-fade-in">
              How It <span className="bg-brand-gradient bg-clip-text text-transparent">Works</span>
            </h2>
            <p className="text-center text-gray-300 text-lg mb-16 max-w-2xl mx-auto">
              Get your trademark registered in just 3 simple steps. We handle everything from start to finish.
            </p>
            
            {/* Desktop Grid View */}
            <div className="hidden lg:grid lg:grid-cols-3 gap-12 relative">
              {/* Connection Lines */}
              <div className="absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-navy-500 via-navy-400 to-navy-500 opacity-30" style={{width: 'calc(100% - 160px)', left: '80px'}}></div>
              
              {processSteps.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <div key={idx} className="relative animate-scale-in" style={{animationDelay: `${idx * 0.15}s`}}>
                    {/* Step Number Badge */}
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-brand-gradient text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg shadow-lg z-10">
                      {step.number}
                    </div>
                    
                    {/* Card */}
                    <div className="bg-navy-700 rounded-2xl p-8 pt-12 shadow-xl hover:shadow-2xl transition-all duration-500 group border-2 border-navy-500/30 hover:border-gold-400/50 relative overflow-hidden">
                      {/* Decorative Background */}
                      <div className="absolute top-0 right-0 w-32 h-32 bg-brand-gradient opacity-5 rounded-bl-full transform translate-x-8 -translate-y-8 group-hover:scale-150 transition-transform duration-500"></div>
                      
                      {/* Icon */}
                      <div className="relative mb-6 flex justify-center">
                        <div className="bg-gradient-to-br from-navy-600 to-navy-500 w-24 h-24 rounded-2xl flex items-center justify-center group-hover:rotate-6 group-hover:scale-110 transition-all duration-500 shadow-md">
                          <Icon className="text-brand-primary animate-pulse-slow" size={40} />
                        </div>
                      </div>
                      
                      {/* Content */}
                      <h3 className="text-2xl font-bold text-gray-100 mb-4 text-center group-hover:text-brand-primary transition-colors">{step.title}</h3>
                      <p className="text-gray-300 leading-relaxed text-center">{step.desc}</p>
                      
                      {/* Bottom Accent */}
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-brand-gradient transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Mobile Slider View */}
            <div className="lg:hidden">
              <MobileSlider autoplay={true} showArrows={true} showDots={true}>
                {processSteps.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <div key={idx} className="relative animate-scale-in" style={{animationDelay: `${idx * 0.15}s`}}>
                    {/* Step Number Badge */}
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-brand-gradient text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg shadow-lg z-10">
                      {step.number}
                    </div>
                    
                    {/* Card */}
                    <div className="bg-navy-700 rounded-2xl p-8 pt-12 shadow-xl hover:shadow-2xl transition-all duration-500 group border-2 border-navy-500/30 hover:border-gold-400/50 relative overflow-hidden">
                      {/* Decorative Background */}
                      <div className="absolute top-0 right-0 w-32 h-32 bg-brand-gradient opacity-5 rounded-bl-full transform translate-x-8 -translate-y-8 group-hover:scale-150 transition-transform duration-500"></div>
                      
                      {/* Icon */}
                      <div className="relative mb-6 flex justify-center">
                        <div className="bg-gradient-to-br from-navy-600 to-navy-500 w-24 h-24 rounded-2xl flex items-center justify-center group-hover:rotate-6 group-hover:scale-110 transition-all duration-500 shadow-md">
                          <Icon className="text-brand-primary animate-pulse-slow" size={40} />
                        </div>
                      </div>
                      
                      {/* Content */}
                      <h3 className="text-2xl font-bold text-gray-100 mb-4 text-center group-hover:text-brand-primary transition-colors">{step.title}</h3>
                      <p className="text-gray-300 leading-relaxed text-center">{step.desc}</p>
                      
                      {/* Bottom Accent */}
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-brand-gradient transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                    </div>
                  </div>
                );
              })}
              </MobileSlider>
            </div>
            
            {/* CTA Below Process */}
            <div className="text-center mt-12">
              <Button
                onClick={() => setIsModalOpen(true)}
                size="lg"
                className="bg-brand-gradient text-white hover:opacity-90 shadow-xl px-10 py-6 text-lg font-bold animate-pulse-glow"
              >
                Start Your Registration Now
                <ArrowRight className="ml-2" size={24} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-navy-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-gray-100 mb-4 animate-fade-in">
              Transparent <span className="bg-brand-gradient bg-clip-text text-transparent">Pricing</span>
            </h2>
            <p className="text-center text-gray-300 mb-12 text-lg">Choose the package that best fits your needs</p>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { name: 'Basic', price: 45, features: ['Case Review', 'Case Filing', 'Basic Search', 'State E-Filing', 'Trademark Secured'] },
                { name: 'Deluxe', price: 145, features: ['Case Review', 'Case Filing', 'Basic Search', 'State E-Filing', 'Trademark Secured', 'Direct Hit Search', 'Dedicated Case Manager'] },
                { name: 'Premium', price: 299, popular: true, features: ['Case Review (by a TM Expert)', 'Basic Free Trademark Search', 'Trademark Monitoring Services (3 Months)', 'Trademark Secured', 'Refusal Risk Meter', 'Case filing service with complete paperwork'] },
                { name: 'Elite', price: 599, features: ['Case Review & Filing (U.S Registered IP Attorney)', 'Assigned Case Filing Officer', 'Private Registration', 'Trademark Secured', 'Federal Search & Clearance Report (All States)', 'Federal E-Filing (USPTO)', 'Cease & Desist Letter', 'Same Day Filing', 'Trademark Monitoring (1 Year)', 'Direct Hit Search', 'Dedicated Case Manager', '24/7 Secure Online Access', '100% Approval Guarantee'] }
              ].map((plan, idx) => (
                <div key={idx} className={`rounded-2xl p-6 transition-all hover:scale-105 flex flex-col h-full ${plan.popular ? 'bg-gradient-to-br from-gold-400 to-gold-500 text-white shadow-2xl ring-4 ring-gold-400/30' : 'bg-navy-700 shadow-lg border border-navy-500/30'}`}>
                  {plan.popular && (
                    <div className="text-center mb-4">
                      <span className="bg-yellow-400 text-gray-100 px-3 py-1 rounded-full text-xs font-bold">MOST POPULAR</span>
                    </div>
                  )}
                  <h3 className={`text-2xl font-bold text-center mb-2 ${plan.popular ? 'text-white' : 'text-gray-100'}`}>{plan.name}</h3>
                  <div className="text-center mb-6">
                    <span className={`text-4xl font-bold ${plan.popular ? 'text-white' : 'text-brand-primary'}`}>${plan.price}</span>
                  </div>
                  <div className={`flex-grow mb-6 overflow-y-auto max-h-64 pr-2 ${plan.popular ? 'scrollbar-thin-blue' : 'scrollbar-thin'}`}>
                    <div className="space-y-2">
                      {plan.features.map((feature, i) => (
                        <div key={i} className="flex items-start space-x-2">
                          <CheckCircle className={`flex-shrink-0 mt-0.5 ${plan.popular ? 'text-gold-200' : 'text-brand-primary'}`} size={18} />
                          <span className={`text-sm ${plan.popular ? 'text-white' : 'text-gray-300'}`}>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Button
                    onClick={() => setIsModalOpen(true)}
                    className={`w-full mt-auto ${plan.popular ? 'bg-navy-500 text-white hover:bg-navy-400' : 'bg-brand-gradient text-white hover:opacity-90'}`}
                  >
                    Get Started
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Trademark is Important */}
      <section className="py-20 bg-navy-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-100 mb-6 animate-fade-in">
              Why <span className="bg-brand-gradient bg-clip-text text-transparent">Trademark Registration</span> is Crucial
            </h2>
            <p className="text-center text-gray-300 text-lg mb-12 max-w-3xl mx-auto">
              Protecting your brand is not just important—it's essential for your business success and growth.
            </p>
            
            {/* Desktop Grid View */}
            <div className="hidden lg:grid lg:grid-cols-2 gap-8">
              {[
                { 
                  icon: Shield, 
                  title: 'Legal Protection', 
                  desc: 'A registered trademark gives you exclusive rights to use your brand name, logo, or slogan nationwide. It prevents others from using similar marks that could confuse your customers.',
                  color: 'from-blue-500 to-blue-600'
                },
                { 
                  icon: TrendingUp, 
                  title: 'Business Asset', 
                  desc: 'Your trademark becomes a valuable business asset that appreciates over time. It can be licensed, sold, or used as collateral, adding significant value to your company.',
                  color: 'from-gold-400 to-gold-500'
                },
                { 
                  icon: Users, 
                  title: 'Customer Trust', 
                  desc: 'A registered trademark symbol (®) builds credibility and trust with customers. It shows you\'re a legitimate, established business committed to protecting your brand.',
                  color: 'from-purple-500 to-purple-600'
                },
                { 
                  icon: Award, 
                  title: 'Competitive Advantage', 
                  desc: 'Stand out from competitors and prevent market confusion. Your trademark stops others from copying your brand identity and riding on your reputation.',
                  color: 'from-orange-500 to-orange-600'
                },
                { 
                  icon: CheckCircle, 
                  title: 'Online Protection', 
                  desc: 'Protect your brand on social media, domain names, and e-commerce platforms. A trademark helps you take down infringing accounts and counterfeit sellers.',
                  color: 'from-gold-400 to-gold-500'
                },
                { 
                  icon: Clock, 
                  title: 'Long-term Rights', 
                  desc: 'Unlike copyrights, trademarks can last forever with proper maintenance. Your brand protection doesn\'t expire—it grows stronger with continued use.',
                  color: 'from-pink-500 to-pink-600'
                }
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div key={idx} className="bg-gradient-to-br from-navy-800 to-navy-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 group animate-scale-in" style={{animationDelay: `${idx * 0.1}s`}}>
                    <div className={`bg-gradient-to-r ${item.color} w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg`}>
                      <Icon className="text-white" size={32} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-100 mb-4 group-hover:text-brand-primary transition-colors">{item.title}</h3>
                    <p className="text-base text-gray-300 leading-relaxed">{item.desc}</p>
                  </div>
                );
              })}
            </div>

            {/* Mobile Slider View */}
            <div className="lg:hidden">
              <MobileSlider autoplay={true} showArrows={true} showDots={true}>
                {[
                  { 
                    icon: Shield, 
                    title: 'Legal Protection', 
                    desc: 'A registered trademark gives you exclusive rights to use your brand name, logo, or slogan nationwide. It prevents others from using similar marks that could confuse your customers.',
                    color: 'from-blue-500 to-blue-600'
                  },
                  { 
                    icon: TrendingUp, 
                    title: 'Business Asset', 
                    desc: 'Your trademark becomes a valuable business asset that appreciates over time. It can be licensed, sold, or used as collateral, adding significant value to your company.',
                    color: 'from-gold-400 to-gold-500'
                  },
                  { 
                    icon: Users, 
                    title: 'Customer Trust', 
                    desc: 'A registered trademark symbol (®) builds credibility and trust with customers. It shows you\'re a legitimate, established business committed to protecting your brand.',
                    color: 'from-purple-500 to-purple-600'
                  },
                  { 
                    icon: Award, 
                    title: 'Competitive Advantage', 
                    desc: 'Stand out from competitors and prevent market confusion. Your trademark stops others from copying your brand identity and riding on your reputation.',
                    color: 'from-orange-500 to-orange-600'
                  },
                  { 
                    icon: CheckCircle, 
                    title: 'Online Protection', 
                    desc: 'Protect your brand on social media, domain names, and e-commerce platforms. A trademark helps you take down infringing accounts and counterfeit sellers.',
                    color: 'from-gold-400 to-gold-500'
                  },
                  { 
                    icon: Clock, 
                    title: 'Long-term Rights', 
                    desc: 'Unlike copyrights, trademarks can last forever with proper maintenance. Your brand protection doesn\'t expire—it grows stronger with continued use.',
                    color: 'from-pink-500 to-pink-600'
                  }
                ].map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="bg-gradient-to-br from-navy-800 to-navy-700 rounded-2xl p-8 shadow-lg">
                      <div className={`bg-gradient-to-r ${item.color} w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-lg`}>
                        <Icon className="text-white" size={32} />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-100 mb-4">{item.title}</h3>
                      <p className="text-base text-gray-300 leading-relaxed">{item.desc}</p>
                    </div>
                  );
                })}
              </MobileSlider>
            </div>
            
            <div className="text-center mt-12">
              <Button
                onClick={() => setIsModalOpen(true)}
                size="lg"
                className="bg-brand-gradient text-white hover:opacity-90 shadow-xl px-10 py-6 text-lg font-bold"
              >
                Protect Your Brand Today
                <ArrowRight className="ml-2" size={24} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials from Home Page */}
      <TestimonialsSlider />

      {/* FAQs from Home Page */}
      <FAQSection />

      {/* CTA Banner */}
      <section className="py-20 bg-brand-gradient">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Protect Your Brand?
            </h2>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Join thousands of satisfied clients who trust us with their trademark registration. Get started today and secure your business identity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => setIsModalOpen(true)}
                size="lg"
                className="bg-navy-500 text-white hover:bg-navy-400 shadow-xl px-10 py-6 text-lg font-bold"
              >
                Register My Trademark
                <ArrowRight className="ml-2" size={24} />
              </Button>
              <Button
                onClick={openZendeskChat}
                size="lg"
                className="bg-transparent border-2 border-gold-400 text-white hover:bg-navy-700/40 px-10 py-6 text-lg font-bold"
              >
                Chat Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Minimal Footer */}
      <footer className="bg-brand-primary text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0">
            {/* Left - Copyright & Contact */}
            <div className="text-center md:text-left">
              <div className="text-sm text-gray-200 mb-2">
                © 2025 Trademark Nexor. All Rights Reserved.
              </div>
              <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 text-sm text-gray-200">
                <a href="tel:+18446190702" className="hover:text-white transition-colors flex items-center">
                  <Phone size={16} className="mr-2" />
                  +1 (877) 676-4227
                </a>
                <span className="hidden sm:inline text-gray-400">|</span>
                <a href="mailto:info@trademarknexor.com" className="hover:text-white transition-colors flex items-center">
                  <Mail size={16} className="mr-2" />
                  info@trademarknexor.com
                </a>
              </div>
            </div>
            
            {/* Right - Links */}
            <div className="flex items-center space-x-4 text-sm">
              <a href="/privacy-policy" className="text-gray-200 hover:text-white transition-colors duration-300">
                Privacy Policy
              </a>
              <span className="text-gray-400">|</span>
              <a href="/terms-conditions" className="text-gray-200 hover:text-white transition-colors duration-300">
                Terms & Conditions
              </a>
            </div>
          </div>
        </div>
      </footer>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default LandingPage;
